<template>
	<div v-if="selectedStudy && currentTab" class="NutritionReports">
		<h2 class="left-align">View Respondent Nutrition Reports</h2>
		<p class="p1 left-align">
			The table below provides some information about each respondent’s intake
			and allows you to view their Respondent Nutrition Report (RNR). The RNR
			provides information to respondents regarding how their intake on the
			reporting day(s) compares to country-specific dietary guidance and
			nutrient requirements. See more information
			<a
			  aria-label="ASA24 Respondent Nutrition Report info - New Window"
			  href="https://epi.grants.cancer.gov/asa24/respondent/nutrition-report.html#:~:text=How%20are%20ASA24%20Respondent%20Nutrition,they%20are%20pregnant%20or%20lactating"
			  target="_blank"
			>
				here
			</a>.
		</p>
		<br>
		<div class="nutrition-reports-filter-container">
			<v-row>
				<v-col>
					<v-row no-gutters>
						<v-col>
							<p class="p3 left-align">Filter by username</p>
						</v-col>
					</v-row>
					<v-row no-gutters>
						<v-col>
							<v-text-field
							  v-model="filterUserNameValue"
							  :aria-label="'Filter by username: ' + filterUserNameValue"
							  id="filterByUsernameViewNutrition"
							  label="Username"
							  placeholder="Username"
							  ref="firstFocusable"
							  hide-details
							  outlined
							  single-line
							  v-on:keyup.enter="runSearch"
							></v-text-field>
						</v-col>
					</v-row>
				</v-col>
				<v-divider vertical></v-divider>
				<v-col>
					<v-row no-gutters>
						<v-col md="6">
							<p class="p3 left-align">Start Date:</p>
							<v-menu v-model="startDateMenu" :close-on-content-click="false" offset-y tile>
								<template v-slot:activator="{ on, attrs }">
									<v-text-field
									hide-details="auto"
									outlined
									type="date"
									:rules="startDateRules"
									v-model="filterStartDateValue"
									id="form-start-date"
									v-bind="attrs"
									v-on="on"
									@keydown.tab='startDateMenu = false'
									:aria-label="'Start Date: ' + filterStartDateValue"
									v-on:keyup.enter="runSearch"
									></v-text-field>
								</template>
								<v-date-picker v-model="filterStartDateValue" color="#42729B"
									style="margin: 0px; float: left" @input="startDateMenu = false"></v-date-picker>
							</v-menu>
						</v-col>
						<v-col md="6">
							<p class="p3 left-align">End Date:</p>
							<v-menu v-model="endDateMenu" :close-on-content-click="false" offset-y tile>
								<template v-slot:activator="{ on, attrs }">
									<v-text-field
									hide-details="auto"
									outlined
									type="date"
									:rules="endDateRules"
									v-model="filterEndDateValue"
									id="form-end-date"
									v-bind="attrs"
									v-on="on"
									@keydown.tab='startDateMenu = false'
									:aria-label="'End Date: ' + filterEndDateValue"
									v-on:keyup.enter="runSearch"
									></v-text-field>
								</template>
								<v-date-picker v-model="filterEndDateValue" color="#42729B"
									style="margin: 0px; float: left" @input="endDateMenu = false"></v-date-picker>
							</v-menu>
						</v-col>
					</v-row>
				</v-col>
				<v-divider vertical></v-divider>
				<v-col>
					<v-row no-gutters>
						<v-col>
							<p class="p3 left-align">Respondent Request:</p>
						</v-col>
					</v-row>

					<fieldset class="border-0">
						<legend class="d-sr-only">
							Respondent Request:
						</legend>

						<v-row no-gutters>
							<v-col>
								<v-checkbox v-model="completedFilter"
									:label="'Completed'"></v-checkbox>
							</v-col>
							<v-col>
								<v-checkbox v-model="quitFilter"
									:label="'Quit'"></v-checkbox>
							</v-col>
							<v-col>
								<v-btn @click="runSearch()" class="btn-default btn-search" text>Search</v-btn>
								<v-btn @click="clearFilters()" class="btn-default" text>Clear</v-btn>
							</v-col>
						</v-row>

					</fieldset>

				</v-col>
			</v-row>
		</div>
		<br>
		<div v-show="headers.length !== 0" class="nutrition-reports-table-container">
			<v-data-table
			  :headers="headers"
			  :items="recalls"
			  :loading="isLoading"
			  class="elevation-1"
			  item-key="recallId"
				:server-items-length="totalResults"
				:options.sync="dataTableOptions"
				@update:options="onUpdateOptions"
				:page.sync="dataTableOptions.page"
			  hide-default-footer
			>
				<template v-for="h in headers" v-slot:[`header.${h.value}`]="{ header }">
					<v-tooltip :disabled="h.hover === ''" :key="h.value" bottom color="#333">
						<template v-slot:activator="{ on }">
							<span v-on="on" class="columnTitleText" tabIndex="0"
								v-on:keyup.enter="(e) => e.target.click()">{{ h.text }}</span>
						</template>
						<span>{{ h.hover }}</span>
					</v-tooltip>
				</template>
				<template v-slot:header.kCal="{ header }">
					<v-tooltip :disabled="header.hover === ''" :key="header.value" bottom color="#333">
						<template v-slot:activator="{ on }">
							<span v-on="on" class="columnTitleText" tabIndex="0"
								v-on:keyup.enter="(e) => e.target.click()">{{ header.text }} <v-icon color="primary" small>info</v-icon></span>
						</template>
						<span>{{ header.hover }}</span>
					</v-tooltip>
				</template>
				<template v-slot:item.source="{ item }">
					{{ getFormattedRespondentRequest(item.source, item.refused) }}
				</template>
				<template v-slot:item.dateReportedLocal="{ item }">
					{{ getFormattedDate(item.dateReportedLocal) }}
				</template>
				<template v-slot:item.kCal="{ item }">
					{{ (item.kCal).toFixed(3) }}
				</template>
				<template v-slot:item.reportBtn="{ item }">
					<v-btn
					  v-if="!item.age"
					  @click="openCreateReportDialog(item)"
					  class="btn-link-left text-none"
					  text
					>
						Create
					</v-btn>
					<v-btn
					  v-else
					  @click="openViewReportDialog(item)"
					  class="btn-link-left text-none"
					  text
					>
						View
					</v-btn>
				</template>
			</v-data-table>
			<div class="pagination-container">
				<v-row>
					<v-col :cols="2">
						<v-text-field
						  :value="dataTableOptions.itemsPerPage"
						  label="Items per page"
							type="number"
							:min="minItemsPerPage"
							:max="maxItemsPerPage"
							@input="onItemsPerPageTextInput"
						></v-text-field>
					</v-col>
					<v-col :cols="8">
						<v-pagination
						  v-model="dataTableOptions.page"
						  :length="pageCount"
							:total-visible="7"
						></v-pagination>
					</v-col>
					<v-col :cols="2">
						<v-text-field
							:value="footerTotalText"
							disabled
							variant="plain"
						></v-text-field>
					</v-col>
				</v-row>
			</div>
		</div>
		<foe-dialog
		  v-model="viewReportDialog"
		  :dialogW="viewReportDialog"
		  class="view-report-dialog"
		  max-width="600"
		  persistent
		>
			<v-card v-if="rowReportInfo" class="view-report-card">
				<v-card-title class="headline">
					View Nutrition Report
				</v-card-title>
				<v-card-text>
					<p class="p2 left-align">
						Information about respondent <strong>{{ rowReportInfo.userName }}</strong> for reporting date
						<strong>{{ getFormattedDate(rowReportInfo.dateReportedLocal) }}</strong>
					</p>
					<v-autocomplete
					  v-model="rowReportInfo.language"
					  :aria-expanded=isExpanded2
					  :items="getVersionLanguages"
					  @click=checkExpandedTwo
					  @blur=checkExpandedTwo
					  @focus=checkExpandedTwo
					  @update:list-index=checkExpandedTwo
					  @update:search-input=checkExpandedTwo
					  item-text="languageName"
					  item-value="languageValue"
					  label="Report Language"
					  ref="autocomplete2"
					  outlined
					>
						<template v-slot:no-data>
							<div></div>
						</template>
					</v-autocomplete>
				</v-card-text>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn text class="btn-default" @click="viewReportDialog = false">
						Cancel
					</v-btn>
					<v-btn text class="btn-default" @click="viewReport()">
						View Report
					</v-btn>
				</v-card-actions>
			</v-card>
		</foe-dialog>
		<foe-dialog
		  v-model="createReportDialog"
		  :dialogW="createReportDialog"
		  class="create-report-dialog"
		  max-width="600"
		  persistent
		>
			<v-card v-if="rowReportInfo" class="create-report-card">
				<v-card-title class="headline">
					View Nutrition Report
				</v-card-title>
				<v-card-text>
					<p class="p2 left-align">
						Specify information about respondent <strong>{{ rowReportInfo.userName }}</strong> for reporting
						date
						<strong>{{ rowReportInfo.dateReported }}</strong>
					</p>
					<v-autocomplete
					  v-model="rowReportInfo.language"
					  :aria-expanded=isExpanded3
					  :items="getVersionLanguages"
					  @click=checkExpandedThree
					  @blur=checkExpandedThree
					  @focus=checkExpandedThree
					  @update:list-index=checkExpandedThree
					  @update:search-input=checkExpandedThree
					  item-text="languageName"
					  item-value="languageValue"
					  label="Report Language"
					  ref="autocomplete3"
					  outlined
					>
						<template v-slot:no-data>
							<div></div>
						</template>
					</v-autocomplete>
					<v-text-field
					  v-model="rowReportInfo.age"
					  label="Age"
					  hide-details
					  outlined
					  single-line
					></v-text-field>
					<v-radio-group v-model="rowReportInfo.gender" label="Sex:" row>
						<v-radio value="F" label="Female">
						</v-radio>
						<v-radio value="M" label="Male">
						</v-radio>
					</v-radio-group>
					<div v-if="rowReportInfo.gender === 'F'">
						<v-radio-group v-model="rowReportInfo.pregnant" label="Pregnant?" row>
							<v-radio :value="true" label="Yes">
							</v-radio>
							<v-radio :value="false" label="No">
							</v-radio>
						</v-radio-group>
						<v-radio-group v-model="rowReportInfo.breastFeed" label="Breastfeeding?" row>
							<v-radio :value="true" label="Yes">
							</v-radio>
							<v-radio :value="false" label="No">
							</v-radio>
						</v-radio-group>
					</div>
				</v-card-text>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn text class="btn-default" @click="cancelCreateReport()">
						Cancel
					</v-btn>
					<v-btn text class="btn-default" @click="viewReport()">
						View Report
					</v-btn>
				</v-card-actions>
			</v-card>
		</foe-dialog>
	</div>
</template>

<script>
import { analysisApiService } from '@/services/analysisApiService.js';
import { authTokenService } from '@/services/authTokenService.js';
import { utilityService } from '@/services/utilityService.js';
import { moduleService } from '@/services/moduleService.js';
import { RecallStatus } from '@/common/RecallStatus.js';

export default {
	name: 'NutritionReports',
	props: {
	},
	data: () => ({
		studyUuid: '',
		recalls: [],
		page: 1,
		itemsPerPage: 10,
		filterStartDateValue: null,
		filterEndDateValue: null,
		filterUserNameValue: '',
		completedFilter: false,
		quitFilter: false,
		rowReportInfo: null,
		viewReportDialog: false,
		createReportDialog: false,
		isLoading: false,
		isProfileDataCreated: false,
		startDateRules: [(v) => (v !== '') || 'Improper date or missing value(s)'],
		endDateRules: [(v) => (v !== '') || 'Improper date or missing value(s)'],
		startDateMenu: false,
		endDateMenu: false,
		isExpaned2: 'false',
		isExpaned3: 'false',
		totalResults: 0,
		minItemsPerPage: 1,
		maxItemsPerPage: 25,
		dataTableOptions: {
			sortBy: ['userName'],
			sortDesc: [false],
			page: 1,
			itemsPerPage: 15
		},
	}),
	mounted() {
		this.endDateRules.push(v => v >= this.filterStartDateValue || 'Date must be greater than or equal to the start date');

		if (this.$refs.autocomplete2) {
			this.isExpanded2 = this.$refs.autocomplete2.isMenuActive.toString();
		}

		if (this.$refs.autocomplete3) {
			this.isExpanded3 = this.$refs.autocomplete3.isMenuActive.toString();
		}
	},
	watch: {
		isProfileDataCreated: function (newValue, oldValue) {
			if (newValue !== oldValue && newValue === true) {
				new Promise(resolve => setTimeout(resolve, 2000)).then(() => {
					this.loadData();
				});
			}
			this.isProfileDataCreated = false;
		},
		rowReportInfo: function (newVal, oldVal) {
			this.$nextTick(function () {
				// 2021.10.20 - Sebastian Calle assisted with the following idea/solution
				// Code that will run only after the entire view has been rendered
				// var b = document.getElementById('form-study-purpose');
				// b.setAttribute('aria-labelledby', 'form-study-purpose-label')
				// 2021.10.25 - Yevgeniy editted above to target class name and remove aria-labelledby attribute for all elements
				// 2022.06.29 - CHT adding to nutrition reports
				var vRadioGroup = document.getElementsByClassName('v-input--radio-group__input');
				for (let i = 0; i < vRadioGroup.length; i++) {
					vRadioGroup[i].removeAttribute('aria-labelledby');
				}
			});
		}
	},
	computed: {
		headers: function () {
			const headerArr = [
				{ text: 'Username', value: 'userName', hover: '', class: 'data-table-header', align: 'center' },
				{ text: 'Reporting Date', value: 'dateReportedLocal', hover: '', class: 'data-table-header', filter: this.filterDate, align: 'center' },
				{ text: 'Status', value: 'recallStatus', hover: '', class: 'data-table-header', filter: this.filterStatus, align: 'center' },
				{ text: 'Respondent Request?', value: 'source', hover: '', class: 'data-table-header', align: 'center', sortable: false },
				{ text: 'Calories (kCal.)', value: 'kCal', hover: 'The total calories for foods, drinks and supplements.', class: 'data-table-header', align: 'center', sortable: false },
				{ text: 'Number of eating occasions', value: 'numMeals', hover: '', class: 'data-table-header', align: 'center', sortable: false },
				{ text: 'Number of food codes', value: 'numFoodCodes', hover: '', class: 'data-table-header', align: 'center', sortable: false }
			];

			// Only add the RNR column if the study version supports RNR module (No Australia studies)
			if (moduleService.isModuleRNRAllowed(this.selectedStudy.version, '').isModuleAllowed) {
				headerArr.push({ text: 'Nutrition Report', value: 'reportBtn', hover: '', class: 'data-table-header', sortable: false, align: 'center' });
			}

			return headerArr;
		},
		selectedStudy: function () {
			// If the selectedStudy changes, the radio buttons and checkbox properties need to be updated.
			if (this.studyUuid !== this.$store.getters.getSelectedStudy.studyUuid) {
				this.updateSelected();
			}
			return this.$store.getters.getSelectedStudy;
		},
		currentTab: function () {
			const tabPosition = {
				tabIndex: parseInt(this.$store.getters.getCurrentStudyTabIndex),
				subTabIndex: parseInt(this.$store.getters.getCurrentStudySubTabIndex)
			};

			this.checkTabMatch(tabPosition);

			return tabPosition;
		},
		getVersionLanguages: function () {
			const versionId = this.selectedStudy.version;
			return this.$store.getters.getStudyVersion(versionId).languages;
		},
		pageCount: function () {
			if (this.dataTableOptions) {
				return this.calculatePageCount(this.dataTableOptions.itemsPerPage);
			}
			return 0;
		},
		footerTotalText: function () {
			if (this.recalls.length) {
				var startPos = (this.dataTableOptions.page - 1) * this.dataTableOptions.itemsPerPage;
				startPos++;
				var endPos;

				if (this.dataTableOptions.page == this.pageCount) {
					endPos = this.totalResults;
				} else {
					endPos = this.dataTableOptions.page * this.dataTableOptions.itemsPerPage;
				}

				var total = this.totalResults;
				return startPos + '-' + endPos + " of " + total;
			} else {
				return '0';
			}
		}
	},
	methods: {
		updateSelected() {
			this.studyUuid = this.$store.getters.getSelectedStudy.studyUuid;
			this.loadData();
		},
		async loadData() {
			this.isLoading = true;
			var rawRecalls;
			const me = this;

			const page = this.dataTableOptions.page;
			const pageSize = this.dataTableOptions.itemsPerPage;
			const sortDir = this.dataTableOptions.sortDesc[0] ? 'DESC' : 'ASC';

			// Handle setting a default sort field if not picked
			let sortBy = 'userName';
			if (this.dataTableOptions.sortBy != null && this.dataTableOptions.sortBy.length > 0) {
				sortBy = this.dataTableOptions.sortBy[0];

				// displayed date is local date, this variable is for another variable in the api call
				if (sortBy === "dateReportedLocal") {
					sortBy = "dateReported";
				}
			}

			// Create an array of recall statuses
			let recallStatuses = [];
			if (this.completedFilter || this.quitFilter) {
				if (this.completedFilter) {
					recallStatuses.push(RecallStatus.Completed);
				}
				if (this.quitFilter) {
					recallStatuses.push(RecallStatus.Quit);
				}
			} else {
				recallStatuses = null;
			}

			await analysisApiService.loadRecalls(this.selectedStudy.studyId, page, pageSize, sortBy, sortDir, this.filterUserNameValue, this.filterStartDateValue, this.filterEndDateValue, recallStatuses)
				.then(async function (response) {
					if (response && response.data) {
						rawRecalls = response.data.trackDataList;
						me.totalResults = response.data.totalResults
					}
				}).catch(function (err) {
					me.isLoading = false;
					utilityService.showErrorMessage(err);
					me.totalResults = 0;
				});

			this.recalls = this.translateEnums(rawRecalls);
			this.isLoading = false;
		},
		async checkTabMatch({ tabIndex, subTabIndex } = {}) {
			var tabsPos = await utilityService.getTabsPositionByName('View Respondent Nutrition Reports', this.$studyTabConfig);
			if (tabsPos.tabPos === tabIndex && tabsPos.subtabPos === subTabIndex) {
				this.$refs.firstFocusable.focus();
				this.loadData();
			}
		},
		onUpdateOptions: function () {
			this.loadData();
		},
		runSearch () {
			// When running a new search, reset the page to 1
			this.dataTableOptions.page = 1;
			this.loadData();
		},
		filterStatus(val) {
			if (!this.completedFilter && !this.quitFilter) {
				return true;
			}

			if (this.completedFilter && this.quitFilter) {
				return true;
			}

			if (this.completedFilter) {
				return val.toLowerCase().includes('completed');
			}

			if (this.quitFilter) {
				return val.toLowerCase().includes('quit');
			}
		},
		filterDate(val) {
			if (!this.filterStartDateValue && !this.filterEndDateValue) {
				return true;
			}

			const selectedStartDate = new Date(this.filterStartDateValue + 'T00:00:00Z').getTime();
			const selectedEndDate = new Date(this.filterEndDateValue + 'T00:00:00Z').getTime() + 86399999; // adding 1 less millisecond of 24 hours of milliseconds (86400000) is essentially the 'end' timestamp for that date's range of miliseconds
			const recallDate = new Date(val).getTime();

			if (this.filterStartDateValue && this.filterEndDateValue) {
				return (recallDate <= selectedEndDate ? (recallDate >= selectedStartDate) : false);
			}

			if (this.filterStartDateValue) {
				return recallDate >= selectedStartDate;
			}

			if (this.filterEndDateValue) {
				return recallDate <= selectedEndDate;
			}
		},
		clearFilters() {
			this.filterUserNameValue = '';
			this.completedFilter = false;
			this.quitFilter = false;
			this.filterStartDateValue = null;
			this.filterEndDateValue = null;
			this.dataTableOptions.page = 1;
			this.loadData();
		},
		translateEnums(rawRecalls) {
			for (var i = 0; i < rawRecalls.length; i++) {
				switch (parseInt(rawRecalls[i].language)) {
					case 0:
						rawRecalls[i].language = 'Unknown';
						break;
					case 1:
						rawRecalls[i].language = 'English';
						break;
					case 2:
						rawRecalls[i].language = 'Spanish';
						break;
					case 3:
						rawRecalls[i].language = 'French';
						break;
					case 4:
						rawRecalls[i].language = 'Polish';
						break;
				}

				switch (rawRecalls[i].recallStatus) {
					case 5:
						rawRecalls[i].recallStatus = 'Quit';
						break;
					case 2:
						rawRecalls[i].recallStatus = 'Completed';
						break;
				}

				rawRecalls[i].dateReported = rawRecalls[i].dateReported.slice(0, rawRecalls[i].dateReported.indexOf('T'));
			}

			return rawRecalls;
		},
		openViewReportDialog(rowInfo) {
			this.rowReportInfo = rowInfo;
			this.viewReportDialog = true;
		},
		openCreateReportDialog(rowInfo) {
			this.rowReportInfo = rowInfo;
			this.createReportDialog = true;
		},
		async viewReport() {
			// Make the url for the RNR
			var rnrLink = this.makeRnrReport();

			// Open the RNR in a new window
			window.open(rnrLink);

			// Close the dialog
			this.viewReportDialog = false;
			this.createReportDialog = false;

			// Flip our watcher flag to reload the data after profile data is created
			this.isProfileDataCreated = true;
		},
		cancelCreateReport() {
			this.createReportDialog = false;
			this.rowReportInfo.age = null;
			this.rowReportInfo.gender = null;
			this.rowReportInfo.breastFeed = false;
			this.rowReportInfo.pregnant = false;
		},
		makeRnrReport() {
			const rnrUrl = `${utilityService.getRespondentUiUrl()}?researcher=true` +
				`&recallId=${this.rowReportInfo.recallId}` +
				`&studyVersion=${this.selectedStudy.version}` +
				`&language=${this.rowReportInfo.language}` +
				`&age=${this.rowReportInfo.age}` +
				`&breastFeed=${this.rowReportInfo.breastFeed}` +
				`&pregnant=${this.rowReportInfo.pregnant}` +
				'&energyProfile=MODERATE' +
				'&source=RESEARCHER' +
				`&gender=${this.rowReportInfo.gender}` +
				`&token=${authTokenService.getToken()}`;

			return rnrUrl;
		},
		getFormattedDate(date) {
			if (date && date.length) {
				return utilityService.formatDate(date)
			}

			return 'N/A';
		},
		getFormattedRespondentRequest(source, isRefused) {
			if (source === 'RESPONDENT' && !isRefused) {
				return 'Yes';
			} else if (source === 'RESPONDENT' && isRefused) {
				return 'Refused';
			}
			return 'No';
		},
		checkExpandedTwo: function () {
			this.isExpanded2 = this.$refs.autocomplete2.isMenuActive.toString()
		},
		checkExpandedThree: function () {
			this.isExpanded3 = this.$refs.autocomplete3.isMenuActive.toString()
		},
		/**
		 * Handles the manual typing of the page size event
		 * @param {*} event - The raised event that holds the entered page size
		 */
		 onItemsPerPageTextInput: function (event) {
			let parsedItemsPerPage = parseInt(event, 10);
			if (!isNaN(parsedItemsPerPage)) {
				// If the entered page size is greater than the total number of recalls, set the page size to the total number of recalls
				if (parsedItemsPerPage > this.maxItemsPerPage) {
					parsedItemsPerPage = this.maxItemsPerPage;
				}

				// If the entered page size is less than the minimum page size, set the page size to the minimum page size
				if (parsedItemsPerPage < this.minItemsPerPage) {
					parsedItemsPerPage = this.minItemsPerPage;
				}

				this.itemsPerPage = parsedItemsPerPage;
				// If the items per page is increasing there's a possibility the selected page is out of range
				if (parsedItemsPerPage > this.dataTableOptions.itemsPerPage) {
					// if it is out of range shift the value to the last page of the new count
					const newPageCount = this.calculatePageCount(parsedItemsPerPage);
					if (this.dataTableOptions.page > newPageCount) {
						this.dataTableOptions.page = newPageCount;
					}
				}

				this.dataTableOptions.itemsPerPage = parsedItemsPerPage;
			}
		},
		calculatePageCount (itemsPerPage) {
			const countRecalls = this.totalResults ? this.totalResults : 0;
			return Math.ceil(countRecalls / itemsPerPage);
		},
		runSearch () {
			// When running a new search, reset the page to 1
			this.dataTableOptions.page = 1;
			this.loadData();
		}
	}
}
</script>

<style></style>
